import { ChangeEvent, FC, FocusEvent, ReactNode, useMemo, useState } from "react";

import { HintMessage } from "components/Hint/Hint";
import TextInput from "components/TextInput/TextInput";
import { ValueOrArray } from "config/types";
import { useTranslation } from "modules/hooks/useTranslation";

import classNames from "classnames";

type PasswordHint = {
  text?: string | ReactNode;
};

export type PasswordHintInput = PasswordHint & {
  status?: "error" | "success";
};

interface IProps {
  value: string;
  id: string;
  className?: string;
  label?: string;
  activeLabel?: string;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: ValueOrArray<string>;
  required?: boolean;
  hint?: PasswordHintInput;
  validations?: unknown[];
  dataTestId?: string;
  onChange?(event: ChangeEvent<HTMLInputElement>): void;
  onFocus?(event: FocusEvent<HTMLInputElement>): void;
  onBlur?(event: FocusEvent<HTMLInputElement>): void;
}

const PasswordInput: FC<IProps> = (props: IProps) => {
  const {
    activeLabel,
    className,
    disabled,
    error,
    errorMessage,
    id,
    label,
    value,
    onBlur,
    onChange,
    onFocus,
    dataTestId,
  } = props;
  const [inputType, setInputType] = useState("password");
  const { t } = useTranslation();

  const toggleInputType = () => {
    return inputType === "password" ? setInputType("text") : setInputType("password");
  };

  const showHideIcon = inputType === "password" ? "Hide" : "Show";
  const icon = value ? showHideIcon : undefined;
  const classes = classNames("form__input", className);

  const hintMessage = useMemo<HintMessage | undefined>(() => {
    if (props.hint) {
      const passwordHintText = t("inputs.password.hint", "Must be at least 12 characters");
      const isError = props.hint.status === "error";
      const isSuccess = props.hint.status === "success";
      return {
        text: {
          className: {
            "color-success-dark": isSuccess,
            "color-error-dark": isError,
          },
          text: passwordHintText,
        },
        icon: {
          src: isSuccess ? "CheckCircle" : "Locked",
          size: "xs",
          color: isSuccess ? "success-dark" : isError ? "error-dark" : "secondary-text",
        },
      };
    }
  }, [props.hint, t]);

  return (
    <TextInput
      className={classes}
      type={inputType}
      {...(icon ? { iconRight: { src: icon } } : {})}
      id={id || "password"}
      label={label ?? t("inputs.password.label", "Password")}
      activeLabel={activeLabel ?? t("inputs.password.activeLabel", "Password")}
      value={value}
      error={error}
      errorMessage={errorMessage}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
      onIconRightClick={() => toggleInputType()}
      disabled={disabled}
      dataTestId={dataTestId}
      hintMessage={hintMessage}
    />
  );
};

export default PasswordInput;
